import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Loader2,
  Megaphone,
  Moon,
  RefreshCw,
  Search,
  Sun,
  Users,
} from "lucide-react";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import LiveContestSidebar, { WISHAUR_RED } from "components/LiveContestSidebar";
import ContestProductControls from "components/ContestProductControls";
import { AnimatedLiveCounter } from "components/AnimatedLiveCounter";
import { useGetProductImagesQuery } from "state/api";
import { getImageUrl } from "utils/common";
import {
  initialsFromName,
  maskPhone,
} from "utils/prizeDisplay";

const PLAY_STORE =
  "https://play.google.com/store/apps/details?id=com.wiwili.wishaur";
const APP_STORE = "https://apps.apple.com/in/search?term=WishAur";
const qrSrc = (url) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(url)}`;
const ANDROID_QR_SRC = qrSrc(PLAY_STORE);
const IOS_QR_SRC = qrSrc(APP_STORE);

const DEFAULT_TICKER =
  "Follow us on YouTube and Instagram · Play free on WishAur · Win Without Losing";

const THEME_STORAGE_KEY = "wishaur-live-board-theme";

const PANEL_TONES = {
  light: {
    rose: {
      panel: "linear-gradient(165deg, rgba(255,241,242,0.96) 0%, rgba(255,255,255,0.9) 100%)",
      header: "rgba(254,226,226,0.75)",
      border: "rgba(211,47,47,0.18)",
      bar: "linear-gradient(90deg, #D32F2F, #fb7185)",
      hintBg: "rgba(211,47,47,0.12)",
      hintText: "#b91c1c",
    },
    amber: {
      panel: "linear-gradient(165deg, rgba(255,247,237,0.96) 0%, rgba(255,255,255,0.9) 100%)",
      header: "rgba(254,215,170,0.45)",
      border: "rgba(234,88,12,0.2)",
      bar: "linear-gradient(90deg, #ea580c, #fbbf24)",
      hintBg: "rgba(234,88,12,0.12)",
      hintText: "#c2410c",
    },
    teal: {
      panel: "linear-gradient(165deg, rgba(240,253,250,0.96) 0%, rgba(255,255,255,0.9) 100%)",
      header: "rgba(153,246,228,0.4)",
      border: "rgba(13,148,136,0.2)",
      bar: "linear-gradient(90deg, #0d9488, #2dd4bf)",
      hintBg: "rgba(13,148,136,0.12)",
      hintText: "#0f766e",
    },
    sky: {
      panel: "linear-gradient(165deg, rgba(240,249,255,0.96) 0%, rgba(255,255,255,0.9) 100%)",
      header: "rgba(186,230,253,0.45)",
      border: "rgba(2,132,199,0.2)",
      bar: "linear-gradient(90deg, #0284c7, #38bdf8)",
      hintBg: "rgba(2,132,199,0.12)",
      hintText: "#0369a1",
    },
    emerald: {
      panel: "linear-gradient(165deg, rgba(236,253,245,0.96) 0%, rgba(255,255,255,0.9) 100%)",
      header: "rgba(167,243,208,0.4)",
      border: "rgba(5,150,105,0.2)",
      bar: "linear-gradient(90deg, #059669, #34d399)",
      hintBg: "rgba(5,150,105,0.12)",
      hintText: "#047857",
    },
    ink: {
      panel: "linear-gradient(165deg, rgba(241,245,249,0.96) 0%, rgba(255,255,255,0.92) 100%)",
      header: "rgba(226,232,240,0.7)",
      border: "rgba(15,23,42,0.12)",
      bar: "linear-gradient(90deg, #334155, #94a3b8)",
      hintBg: "rgba(15,23,42,0.08)",
      hintText: "#475569",
    },
  },
  dark: {
    rose: {
      panel: "linear-gradient(165deg, rgba(69,10,10,0.55) 0%, rgba(20,26,38,0.94) 100%)",
      header: "rgba(127,29,29,0.45)",
      border: "rgba(248,113,113,0.22)",
      bar: "linear-gradient(90deg, #D32F2F, #fb7185)",
      hintBg: "rgba(248,113,113,0.15)",
      hintText: "#fecaca",
    },
    amber: {
      panel: "linear-gradient(165deg, rgba(67,20,7,0.5) 0%, rgba(20,26,38,0.94) 100%)",
      header: "rgba(154,52,18,0.4)",
      border: "rgba(251,146,60,0.22)",
      bar: "linear-gradient(90deg, #ea580c, #fbbf24)",
      hintBg: "rgba(251,146,60,0.15)",
      hintText: "#fed7aa",
    },
    teal: {
      panel: "linear-gradient(165deg, rgba(4,47,46,0.5) 0%, rgba(20,26,38,0.94) 100%)",
      header: "rgba(19,78,74,0.45)",
      border: "rgba(45,212,191,0.22)",
      bar: "linear-gradient(90deg, #0d9488, #2dd4bf)",
      hintBg: "rgba(45,212,191,0.15)",
      hintText: "#99f6e4",
    },
    sky: {
      panel: "linear-gradient(165deg, rgba(12,47,74,0.5) 0%, rgba(20,26,38,0.94) 100%)",
      header: "rgba(7,89,133,0.4)",
      border: "rgba(56,189,248,0.22)",
      bar: "linear-gradient(90deg, #0284c7, #38bdf8)",
      hintBg: "rgba(56,189,248,0.15)",
      hintText: "#bae6fd",
    },
    emerald: {
      panel: "linear-gradient(165deg, rgba(6,46,32,0.5) 0%, rgba(20,26,38,0.94) 100%)",
      header: "rgba(6,78,59,0.45)",
      border: "rgba(52,211,153,0.22)",
      bar: "linear-gradient(90deg, #059669, #34d399)",
      hintBg: "rgba(52,211,153,0.15)",
      hintText: "#a7f3d0",
    },
    ink: {
      panel: "linear-gradient(165deg, rgba(30,41,59,0.7) 0%, rgba(20,26,38,0.94) 100%)",
      header: "rgba(51,65,85,0.55)",
      border: "rgba(148,163,184,0.2)",
      bar: "linear-gradient(90deg, #64748b, #94a3b8)",
      hintBg: "rgba(148,163,184,0.12)",
      hintText: "#cbd5e1",
    },
  },
};

const THEMES = {
  light: {
    pageBg: "#e7d5d0",
    pageGradient:
      "radial-gradient(1100px 560px at 0% -5%, rgba(211,47,47,0.28), transparent 52%), radial-gradient(900px 500px at 100% 5%, rgba(14,165,233,0.18), transparent 48%), radial-gradient(800px 420px at 55% 100%, rgba(16,185,129,0.14), transparent 50%), linear-gradient(160deg, #f8e9e6 0%, #e8eef8 42%, #e4f3ef 100%)",
    panelBg: "rgba(255,255,255,0.88)",
    panelBorder: "rgba(15,23,42,0.08)",
    panelShadow: "0 12px 32px rgba(127,29,29,0.08), 0 8px 24px rgba(15,23,42,0.06)",
    headerBg: "rgba(248,250,252,0.92)",
    headerBorder: "rgba(15,23,42,0.06)",
    title: "#0f172a",
    hint: "#64748b",
    text: "#0f172a",
    muted: "#64748b",
    track: "rgba(15,23,42,0.06)",
    controlsBg: "rgba(255,255,255,0.94)",
    controlsBorder: "rgba(15,23,42,0.08)",
    avatarBg: "#fecdd3",
    avatarText: "#9f1239",
    productPlaceholder: "#fecdd3",
    qrBorder: "rgba(15,23,42,0.1)",
    qrLabel: "#0f172a",
    accent: WISHAUR_RED,
    accentSoft: "rgba(211,47,47,0.12)",
    liveGlow: "rgba(211,47,47,0.45)",
  },
  dark: {
    pageBg: "#070b14",
    pageGradient:
      "radial-gradient(1000px 480px at 10% -8%, rgba(211,47,47,0.28), transparent 55%), radial-gradient(800px 420px at 90% 0%, rgba(14,165,233,0.14), transparent 50%), radial-gradient(700px 380px at 50% 100%, rgba(16,185,129,0.1), transparent 50%), linear-gradient(165deg, #0b1220 0%, #0a101c 50%, #070b14 100%)",
    panelBg: "rgba(20,26,38,0.92)",
    panelBorder: "rgba(148,163,184,0.14)",
    panelShadow: "0 14px 36px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.04) inset",
    headerBg: "rgba(15,23,42,0.88)",
    headerBorder: "rgba(148,163,184,0.12)",
    title: "#e2e8f0",
    hint: "#94a3b8",
    text: "#f8fafc",
    muted: "#94a3b8",
    track: "rgba(148,163,184,0.12)",
    controlsBg: "rgba(20,26,38,0.96)",
    controlsBorder: "rgba(148,163,184,0.14)",
    avatarBg: "#1e293b",
    avatarText: "#cbd5e1",
    productPlaceholder: "#1e293b",
    qrBorder: "rgba(148,163,184,0.2)",
    qrLabel: "#e2e8f0",
    accent: WISHAUR_RED,
    accentSoft: "rgba(211,47,47,0.2)",
    liveGlow: "rgba(211,47,47,0.55)",
  },
};

const relativeJoin = (iso) => {
  if (!iso) return "";
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return "Just now";
  if (min === 1) return "1 min ago";
  if (min < 60) return `${min} min ago`;
  const hrs = Math.floor(min / 60);
  return hrs === 1 ? "1 hr ago" : `${hrs} hrs ago`;
};

const Panel = ({
  title,
  hint,
  children,
  className = "",
  bodyClassName = "",
  compactHeader = false,
  theme,
  tone = null,
  isDark = false,
}) => {
  const toneSet = PANEL_TONES[isDark ? "dark" : "light"];
  const tint = (tone && toneSet[tone]) || null;
  return (
    <section
      className={`relative rounded-2xl border overflow-hidden flex flex-col min-h-0 backdrop-blur-md ${className}`}
      style={{
        background: tint?.panel || theme.panelBg,
        borderColor: tint?.border || theme.panelBorder,
        boxShadow: theme.panelShadow,
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-[3px] pointer-events-none"
        style={{
          background:
            tint?.bar ||
            `linear-gradient(90deg, ${theme.accentSoft}, transparent 70%)`,
        }}
      />
      <header
        className={`flex items-center justify-between gap-2 border-b shrink-0 ${
          compactHeader ? "px-2.5 py-1.5" : "px-3 py-2"
        }`}
        style={{
          borderColor: tint?.border || theme.headerBorder,
          background: tint?.header || theme.headerBg,
        }}
      >
        <h3
          className="text-[11px] font-bold tracking-[0.14em] uppercase truncate"
          style={{ color: theme.title, fontFamily: "var(--live-font-display)" }}
        >
          {title}
        </h3>
        {hint ? (
          <span
            className="text-[9px] font-semibold uppercase shrink-0 px-1.5 py-0.5 rounded-md"
            style={{
              color: tint?.hintText || theme.hint,
              backgroundColor: tint?.hintBg || theme.track,
            }}
          >
            {hint}
          </span>
        ) : null}
      </header>
      <div className={`flex-1 min-h-0 ${bodyClassName || "p-3"}`}>{children}</div>
    </section>
  );
};

const JoinAvatar = ({ image, name, theme }) => {
  const [err, setErr] = useState(false);
  const src = getImageUrl(image);
  const label = String(name || "User").trim() || "User";
  return (
    <div
      className="w-8 h-8 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-[10px] font-bold"
      style={{ backgroundColor: theme.avatarBg, color: theme.avatarText }}
    >
      {src && !err ? (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          onError={() => setErr(true)}
        />
      ) : (
        initialsFromName(label)
      )}
    </div>
  );
};

const LiveQuizView = ({
  product,
  selectedWish,
  history,
  uniqueUserCount = 0,
  historyLoading = false,
  onClose,
  showBackButton = false,
  embedded = false,
  onRefresh,
  refreshing = false,
  refreshTrigger = 0,
  realtimeConnected = false,
  dashboardStats,
  productActive = true,
  onToggleProductActive,
  togglingProductStatus = false,
  productPlayable = true,
  onToggleProductPlayable,
  togglingPlayableStatus = false,
  onConfirmAnnounceResult,
  announcingResult = false,
  canAnnounceResult = false,
  tickerMessage = DEFAULT_TICKER,
}) => {
  const [announceDialogOpen, setAnnounceDialogOpen] = useState(false);
  const [showAdminControls, setShowAdminControls] = useState(false);
  const [rankLookupInput, setRankLookupInput] = useState("");
  const [viewRank, setViewRank] = useState(null);
  const [showRankLookup, setShowRankLookup] = useState(true);
  const [boardTheme, setBoardTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      return saved === "dark" ? "dark" : "light";
    } catch {
      return "light";
    }
  });
  const theme = THEMES[boardTheme] || THEMES.light;
  const isDark = boardTheme === "dark";

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, boardTheme);
    } catch {
      // ignore storage errors
    }
  }, [boardTheme]);

  useEffect(() => {
    setViewRank(null);
    setRankLookupInput("");
  }, [product?._id, selectedWish?.playToken]);

  useEffect(() => {
    const id = "wishaur-live-board-fonts";
    if (document.getElementById(id)) return undefined;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800;900&family=Sora:wght@500;600;700;800&display=swap";
    document.head.appendChild(link);
    return undefined;
  }, []);

  const isContestMode = Boolean(product && selectedWish);
  const contestName = product?.productName || "Contest";
  const contestStatus = selectedWish?.status || "LIVE";
  const fillWish = selectedWish?.fillWish ?? 0;
  const totalWish = product?.totalWish ? Number(product.totalWish) : null;
  const statusUpper = (contestStatus || "").toUpperCase();
  const showAnnounce =
    canAnnounceResult &&
    onConfirmAnnounceResult &&
    (statusUpper === "ONGOING" ||
      statusUpper === "PENDINGRESULT" ||
      statusUpper === "PENDINGRESULT_EVENT");
  const hasAdminControls = Boolean(
    onToggleProductActive || onToggleProductPlayable || showAnnounce
  );

  const youtubeVideoId = String(selectedWish?.youtubeVideoId || "").trim();
  const lastJoined = dashboardStats?.lastJoined ?? [];

  const { data: imagesData } = useGetProductImagesQuery(product?._id, {
    skip: !product?._id,
  });
  const productImage = useMemo(() => {
    const list = Array.isArray(imagesData) ? imagesData : imagesData?.data || [];
    if (!list.length) return null;
    const sorted = [...list].sort((a, b) => {
      const ta = new Date(a.timestamp || 0).getTime();
      const tb = new Date(b.timestamp || 0).getTime();
      return tb - ta;
    });
    const withTime = list.filter((img) => img.timestamp);
    const pick = withTime.length ? sorted[0] : list[list.length - 1];
    return getImageUrl(pick?.image);
  }, [imagesData]);

  const handleConfirmAnnounce = async () => {
    if (!onConfirmAnnounceResult) return;
    try {
      await onConfirmAnnounceResult();
      setAnnounceDialogOpen(false);
    } catch {
      // Parent shows toast; keep dialog open for retry.
    }
  };

  const applyRankLookup = () => {
    const n = parseInt(String(rankLookupInput).replace(/[, ]/g, ""), 10);
    if (!Number.isFinite(n) || n < 1) return;
    setViewRank(n);
  };

  const resetToMainRank = () => {
    setViewRank(null);
    setRankLookupInput("");
  };

  const rootClass = embedded
    ? "min-h-[100dvh] w-full max-w-[100vw] overflow-x-hidden overflow-y-auto lg:h-[100dvh] lg:overflow-hidden"
    : "min-h-screen w-full overflow-x-hidden overflow-y-auto lg:h-screen lg:overflow-hidden";

  return (
    <div
      className={`relative flex flex-col ${rootClass}`}
      style={{
        backgroundColor: theme.pageBg,
        backgroundImage: theme.pageGradient,
        fontFamily: "var(--live-font-body)",
        ["--live-font-display"]: '"Sora", "Outfit", system-ui, sans-serif',
        ["--live-font-body"]: '"Outfit", system-ui, sans-serif',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230f172a' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
        }}
        aria-hidden
      />
      <style>{`
        @keyframes live-ticker-x {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes live-pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 var(--live-glow); }
          50% { opacity: 0.85; transform: scale(1.05); box-shadow: 0 0 0 6px transparent; }
        }
      `}</style>

      <header
        className="relative sticky top-0 z-20 shrink-0 px-2 sm:px-3 py-2.5 flex items-center justify-between gap-2 shadow-lg"
        style={{
          background: `linear-gradient(105deg, ${WISHAUR_RED} 0%, #b71c1c 55%, #c62828 100%)`,
        }}
      >
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
          {showBackButton && onClose && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 px-2 text-white hover:bg-white/20 hover:text-white shrink-0"
            >
              <ArrowLeft className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          )}
          <div className="min-w-0">
            <h1
              className="text-white font-extrabold text-base sm:text-lg tracking-tight truncate leading-tight"
              style={{ fontFamily: "var(--live-font-display)" }}
            >
              {isContestMode ? contestName : "WishAur Live Dashboard"}
            </h1>
            <p className="text-white/90 text-[11px] truncate flex items-center gap-1.5">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full bg-white shrink-0"
                style={{
                  ["--live-glow"]: theme.liveGlow,
                  animation: "live-pulse-dot 1.8s ease-in-out infinite",
                }}
              />
              Live · {contestStatus}
              {realtimeConnected ? " · realtime" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setBoardTheme((t) => (t === "dark" ? "light" : "dark"))}
            className="h-8 px-2 text-white hover:bg-white/20 hover:text-white"
            title={isDark ? "Switch to light screen" : "Switch to dark screen"}
          >
            {isDark ? (
              <Sun className="h-4 w-4 sm:mr-1" />
            ) : (
              <Moon className="h-4 w-4 sm:mr-1" />
            )}
            <span className="hidden sm:inline text-xs font-semibold">
              {isDark ? "Light" : "Dark"}
            </span>
          </Button>
          {onRefresh && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing}
              className="h-8 px-2 text-white hover:bg-white/20 hover:text-white"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowRankLookup((v) => !v)}
            className="h-8 px-2 text-white hover:bg-white/20 hover:text-white"
            title={showRankLookup ? "Hide rank lookup" : "Show rank lookup"}
          >
            <Search className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline text-xs font-semibold">
              {showRankLookup ? "Hide lookup" : "Show lookup"}
            </span>
          </Button>
          {hasAdminControls && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowAdminControls((v) => !v)}
              className="h-8 px-2 text-white hover:bg-white/20 hover:text-white"
              title={
                showAdminControls
                  ? "Hide admin controls"
                  : "Show admin controls"
              }
            >
              {showAdminControls ? (
                <EyeOff className="h-4 w-4 sm:mr-1" />
              ) : (
                <Eye className="h-4 w-4 sm:mr-1" />
              )}
              <span className="hidden sm:inline text-xs font-semibold">
                {showAdminControls ? "Hide controls" : "Show controls"}
              </span>
            </Button>
          )}
        </div>
      </header>

      <div className="relative z-[1] flex-1 min-h-0 p-2.5 sm:p-3 flex flex-col gap-2.5 lg:overflow-hidden">
        {/*
          Sketch layout:
          [ Product / Entry / Users ] [ Main Rank ] [ Last Five ]
                                      [ YouTube   ] [ QR        ]
          [ Follow us running message (full width)              ]
        */}
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(220px,0.95fr)_minmax(0,1.4fr)_minmax(240px,1fr)] lg:grid-rows-[minmax(0,1.35fr)_minmax(180px,1fr)] gap-2.5">
          {/* Left column — Product, Entries, Users stacked */}
          <div className="grid grid-rows-[1.15fr_1fr_1fr] gap-2.5 min-h-[22rem] lg:min-h-0 lg:row-span-2">
            <Panel title="Product" compactHeader bodyClassName="!p-0" theme={theme} tone="rose" isDark={isDark}>
              <div
                className="relative h-full min-h-[7rem]"
                style={{ backgroundColor: theme.productPlaceholder }}
              >
                {productImage ? (
                  <img
                    src={productImage}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div
                    className="absolute inset-0 flex items-center justify-center text-xs"
                    style={{ color: theme.muted }}
                  >
                    No image
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-3 pt-8 pb-2.5">
                  <p className="text-sm sm:text-base font-black text-white leading-tight uppercase drop-shadow-sm line-clamp-2">
                    {contestName}
                  </p>
                  <span
                    className="inline-block mt-1.5 text-[10px] font-black px-2 py-0.5 rounded text-white"
                    style={{ backgroundColor: WISHAUR_RED }}
                  >
                    {contestStatus}
                  </span>
                </div>
              </div>
            </Panel>

            <Panel
              title="No. of Entry"
              hint="5 sec"
              compactHeader
              bodyClassName="p-3 sm:p-4"
              theme={theme}
              tone="amber"
              isDark={isDark}
            >
              <div className="h-full flex flex-col justify-center gap-2">
                  <p
                  className="font-black tabular-nums leading-none tracking-tight"
                  style={{
                    color: theme.text,
                    fontFamily: "var(--live-font-display)",
                  }}
                >
                  <AnimatedLiveCounter
                    value={fillWish}
                    size="xl"
                    gravity
                    className="!text-[clamp(2rem,4.2vw,3.25rem)] !leading-none overflow-visible"
                  />
                </p>
                {totalWish ? (
                  <>
                    <div
                      className="h-2 w-full rounded-full overflow-hidden"
                      style={{ backgroundColor: theme.track }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(0, (Number(fillWish) / totalWish) * 100)
                          )}%`,
                          backgroundColor: WISHAUR_RED,
                        }}
                      />
                    </div>
                    <p
                      className="text-xs sm:text-sm font-semibold tabular-nums"
                      style={{ color: theme.muted }}
                    >
                      of {totalWish.toLocaleString("en-IN")} target
                    </p>
                  </>
                ) : null}
              </div>
            </Panel>

            <Panel
              title="User Playing"
              hint="5 sec"
              compactHeader
              bodyClassName="p-3 sm:p-4"
              theme={theme}
              tone="sky"
              isDark={isDark}
            >
              <div className="h-full flex flex-col justify-center gap-1.5">
                <div className="flex items-end gap-2.5">
                  <div
                    className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      backgroundColor: isDark ? "rgba(211,47,47,0.2)" : `${WISHAUR_RED}14`,
                    }}
                  >
                    <Users className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: WISHAUR_RED }} />
                  </div>
                  <p
                    className="font-black tabular-nums leading-none tracking-tight"
                    style={{
                      color: theme.text,
                      fontFamily: "var(--live-font-display)",
                    }}
                  >
                    <AnimatedLiveCounter
                      value={uniqueUserCount}
                      size="xl"
                      gravity
                      className="!text-[clamp(2rem,4.2vw,3.25rem)] !leading-none overflow-visible"
                    />
                  </p>
                </div>
                <p
                  className="text-xs sm:text-sm font-semibold uppercase tracking-wide"
                  style={{ color: theme.muted }}
                >
                  Unique users live
                </p>
              </div>
            </Panel>
          </div>

          {/* Center — Main Rank */}
          <Panel
            title="Main Rank"
            hint="10 sec"
            className="min-h-[22rem] lg:min-h-0"
            bodyClassName="!p-0"
            theme={theme}
            tone="teal"
            isDark={isDark}
          >
            {isContestMode ? (
              <LiveContestSidebar
                product={product}
                selectedWish={selectedWish}
                fallbackHistory={history}
                fallbackLoading={historyLoading}
                refreshTrigger={refreshTrigger}
                realtimeConnected={realtimeConnected}
                embedded
                viewRank={viewRank}
                boardTheme={boardTheme}
                className="h-full min-h-0"
                pollingIntervalMs={10_000}
              />
            ) : (
              <div
                className="h-full flex items-center justify-center text-xs p-6"
                style={{ color: theme.muted }}
              >
                Select a contest round to view ranks.
              </div>
            )}
          </Panel>

          {/* Right — Rank lookup (optional) + Last Five */}
          <div
            className={`grid gap-2.5 min-h-[14rem] lg:min-h-0 ${
              showRankLookup
                ? "grid-rows-[auto_minmax(0,1fr)]"
                : "grid-rows-1"
            }`}
          >
            {showRankLookup ? (
              <Panel title="Rank Lookup" compactHeader theme={theme} tone="ink" isDark={isDark}>
                <div className="flex flex-col gap-2.5">
                  <p className="text-[11px] leading-snug" style={{ color: theme.muted }}>
                    Default shows the product main rank. Enter any rank to see that
                    holder.
                  </p>
                  <div className="flex gap-1.5">
                    <input
                      type="number"
                      min={1}
                      inputMode="numeric"
                      placeholder="e.g. 1 or 1000"
                      value={rankLookupInput}
                      onChange={(e) => setRankLookupInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyRankLookup();
                        }
                      }}
                      className="flex-1 min-w-0 rounded-lg border px-2.5 py-2 text-sm font-semibold tabular-nums outline-none focus:ring-2 focus:ring-red-500/30"
                      style={{
                        backgroundColor: theme.track,
                        borderColor: theme.panelBorder,
                        color: theme.text,
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="shrink-0 h-auto px-3"
                      style={{ backgroundColor: WISHAUR_RED }}
                      onClick={applyRankLookup}
                      disabled={!isContestMode}
                    >
                      <Search className="w-3.5 h-3.5 mr-1" />
                      Show
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={resetToMainRank}
                      className="text-[10px] font-bold uppercase px-2 py-1 rounded-md border transition-colors"
                      style={{
                        borderColor: theme.panelBorder,
                        backgroundColor:
                          viewRank == null ? `${WISHAUR_RED}18` : theme.track,
                        color: viewRank == null ? WISHAUR_RED : theme.text,
                      }}
                    >
                      Main
                    </button>
                    {[1, 1000, 5000].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => {
                          setRankLookupInput(String(n));
                          setViewRank(n);
                        }}
                        className="text-[10px] font-bold uppercase px-2 py-1 rounded-md border transition-colors"
                        style={{
                          borderColor: theme.panelBorder,
                          backgroundColor:
                            viewRank === n ? `${WISHAUR_RED}18` : theme.track,
                          color: viewRank === n ? WISHAUR_RED : theme.text,
                        }}
                      >
                        #{n.toLocaleString("en-IN")}
                      </button>
                    ))}
                  </div>
                  {viewRank != null ? (
                    <p className="text-[10px] font-semibold" style={{ color: theme.hint }}>
                      Showing lookup #{Number(viewRank).toLocaleString("en-IN")}
                    </p>
                  ) : (
                    <p className="text-[10px] font-semibold" style={{ color: theme.hint }}>
                      Showing product main rank
                    </p>
                  )}
                </div>
              </Panel>
            ) : null}

            <Panel
              title="Last Five Join"
              hint="5–7 sec"
              className="min-h-0"
              theme={theme}
              tone="emerald"
              isDark={isDark}
            >
              <ul
                className="h-full overflow-y-auto flex flex-col justify-start divide-y"
                style={{ borderColor: theme.headerBorder }}
              >
                {lastJoined.length === 0 ? (
                  <li
                    className="text-xs py-6 text-center"
                    style={{ color: theme.muted }}
                  >
                    No joins yet
                  </li>
                ) : (
                  lastJoined.slice(0, 5).map((row, i) => {
                    const phoneLabel = maskPhone(row.phone);
                    const name = String(row.name || "").trim() || "Player";
                    return (
                      <li
                        key={`${row.userId || "u"}-${row.createdAt || i}`}
                        className="flex items-center gap-2.5 py-2.5 first:pt-0 last:pb-0"
                        style={{ borderColor: theme.headerBorder }}
                      >
                        <JoinAvatar image={row.image} name={name} theme={theme} />
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-xs sm:text-sm font-bold truncate"
                            style={{ color: theme.text }}
                          >
                            {name}
                          </p>
                          {phoneLabel ? (
                            <p
                              className="text-[10px] truncate"
                              style={{ color: theme.muted }}
                            >
                              {phoneLabel}
                            </p>
                          ) : null}
                        </div>
                        <span
                          className="text-[10px] shrink-0"
                          style={{ color: theme.hint }}
                        >
                          {relativeJoin(row.createdAt)}
                        </span>
                      </li>
                    );
                  })
                )}
              </ul>
            </Panel>
          </div>

          {/* Center bottom — YouTube */}
          <Panel
            title="YouTube"
            className="min-h-[12rem] lg:min-h-0"
            bodyClassName="!p-0 bg-black"
            theme={theme}
            tone="ink"
            isDark={isDark}
          >
            <div className="h-full min-h-[12rem] lg:min-h-0 bg-black flex items-center justify-center">
              {youtubeVideoId ? (
                <iframe
                  title="Contest YouTube"
                  src={`https://www.youtube.com/embed/${encodeURIComponent(
                    youtubeVideoId
                  )}?autoplay=1&mute=1&rel=0`}
                  className="w-full h-full min-h-[12rem]"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : null}
            </div>
          </Panel>

          {/* Right bottom — QR */}
          <Panel
            title="QR Code to Download APP"
            className="min-h-[12rem] lg:min-h-0"
            theme={theme}
            tone="amber"
            isDark={isDark}
          >
            <div className="h-full flex items-center justify-center gap-4 sm:gap-6">
              <a
                href={PLAY_STORE}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center gap-2 min-w-0 flex-1 max-w-[200px]"
              >
                <img
                  src={ANDROID_QR_SRC}
                  alt="Android Play Store"
                  className="w-full aspect-square max-h-[min(200px,100%)] object-contain rounded-2xl bg-white p-2.5 border shadow-sm"
                  style={{ borderColor: theme.qrBorder }}
                />
                <span
                  className="text-xs sm:text-sm font-bold uppercase tracking-wider"
                  style={{
                    color: theme.qrLabel,
                    fontFamily: "var(--live-font-display)",
                  }}
                >
                  Android
                </span>
              </a>
              <a
                href={APP_STORE}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center gap-2 min-w-0 flex-1 max-w-[200px]"
              >
                <img
                  src={IOS_QR_SRC}
                  alt="iOS App Store"
                  className="w-full aspect-square max-h-[min(200px,100%)] object-contain rounded-2xl bg-white p-2.5 border shadow-sm"
                  style={{ borderColor: theme.qrBorder }}
                />
                <span
                  className="text-xs sm:text-sm font-bold uppercase tracking-wider"
                  style={{
                    color: theme.qrLabel,
                    fontFamily: "var(--live-font-display)",
                  }}
                >
                  iOS
                </span>
              </a>
            </div>
          </Panel>
        </div>

        {/* Bottom — customisable running message */}
        <aside
          className="shrink-0 rounded-2xl overflow-hidden h-11 flex items-center border"
          style={{
            background: isDark
              ? "linear-gradient(90deg, #111827 0%, #1a1020 50%, #111827 100%)"
              : "linear-gradient(90deg, #0f172a 0%, #1c1218 45%, #0f172a 100%)",
            borderColor: theme.panelBorder,
            boxShadow: theme.panelShadow,
          }}
        >
          <div
            className="shrink-0 h-full px-3 flex items-center text-[10px] font-extrabold uppercase tracking-wider text-white"
            style={{ backgroundColor: WISHAUR_RED }}
          >
            Live
          </div>
          <div className="w-full overflow-hidden whitespace-nowrap">
            <p
              className="inline-block text-[12px] sm:text-sm font-semibold tracking-wide px-4 text-white/95"
              style={{
                animation: "live-ticker-x 22s linear infinite",
                fontFamily: "var(--live-font-body)",
              }}
            >
              {tickerMessage} · {tickerMessage}
            </p>
          </div>
        </aside>
      </div>

      {hasAdminControls && showAdminControls && (
        <div
          className="shrink-0 border-t px-3 py-2 space-y-2"
          style={{
            backgroundColor: theme.controlsBg,
            borderColor: theme.controlsBorder,
          }}
        >
          {(onToggleProductActive || onToggleProductPlayable) && (
            <ContestProductControls
              productActive={productActive}
              onToggleProductActive={onToggleProductActive}
              togglingProductStatus={togglingProductStatus}
              productPlayable={productPlayable}
              onToggleProductPlayable={onToggleProductPlayable}
              togglingPlayableStatus={togglingPlayableStatus}
              accentColor={WISHAUR_RED}
            />
          )}
          {showAnnounce && (
            <Button
              type="button"
              className="w-full h-10 text-sm font-semibold text-white"
              style={{ backgroundColor: WISHAUR_RED }}
              disabled={announcingResult}
              onClick={() => setAnnounceDialogOpen(true)}
            >
              {announcingResult ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Announcing…
                </>
              ) : (
                <>
                  <Megaphone className="h-4 w-4 mr-1.5" />
                  {statusUpper === "ONGOING"
                    ? "Close & announce result"
                    : "Announce result"}
                </>
              )}
            </Button>
          )}
        </div>
      )}

      <Dialog open={announceDialogOpen} onOpenChange={setAnnounceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {statusUpper === "ONGOING" ? "Close & announce result?" : "Announce result?"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-gray-600">
                {statusUpper === "ONGOING" ? (
                  <>
                    <p>
                      This will close the round (PENDINGRESULT), distribute prizes to winners,
                      and mark the round as <strong>PROCESSED</strong>.
                    </p>
                    {selectedWish?.isCurrentRound && (
                      <p>
                        This is the current round — a new ONGOING round will be created and
                        the product will stop accepting entries until re-enabled.
                      </p>
                    )}
                  </>
                ) : (
                  <p>
                    This will immediately process payouts and mark the round as{" "}
                    <strong>PROCESSED</strong> (skips the 10-minute cron wait).
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAnnounceDialogOpen(false)}
              disabled={announcingResult}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmAnnounce}
              disabled={announcingResult}
              style={{ backgroundColor: WISHAUR_RED }}
              className="text-white"
            >
              {announcingResult
                ? "Announcing…"
                : statusUpper === "ONGOING"
                  ? "Close & announce"
                  : "Announce result"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { WISHAUR_RED } from "components/LiveContestSidebar";
export default LiveQuizView;
